import { useCallback, useEffect, useState } from "react";
import {
  getBrowserNotificationPermission,
  isBrowserNotificationSupported,
  requestBrowserNotificationPermission,
} from "../utils/browserNotifications";

const DISMISS_KEY = "flexhr.pushModal.dismissedAt";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function wasDismissedRecently() {
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = Number(raw);
    return Number.isFinite(at) && Date.now() - at < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

/**
 * Tracks Notification.permission and exposes request + force-modal visibility.
 * Modal can be snoozed ("Më vonë") so a denied/default browser does not brick the app.
 */
export function useBrowserPushPermission({ forceModalDelayMs = 1800 } = {}) {
  const supported = isBrowserNotificationSupported();
  const [permission, setPermission] = useState(() =>
    getBrowserNotificationPermission()
  );
  const [forceOpen, setForceOpen] = useState(false);
  const [snoozed, setSnoozed] = useState(() => wasDismissedRecently());

  const refresh = useCallback(() => {
    setPermission(getBrowserNotificationPermission());
  }, []);

  useEffect(() => {
    refresh();
    if (!supported || !navigator?.permissions?.query) return undefined;
    let status;
    let cancelled = false;
    navigator.permissions
      .query({ name: "notifications" })
      .then((result) => {
        if (cancelled) return;
        status = result;
        setPermission(result.state === "prompt" ? "default" : result.state);
        status.onchange = () => {
          setPermission(
            result.state === "prompt" ? "default" : result.state
          );
        };
      })
      .catch(() => {
        // Safari / unsupported — rely on Notification.permission polls
      });
    return () => {
      cancelled = true;
      if (status) status.onchange = null;
    };
  }, [supported, refresh]);

  // Prompt when not granted (after short delay so login UI settles)
  useEffect(() => {
    if (!supported) return undefined;
    if (permission === "granted" || snoozed) {
      setForceOpen(false);
      return undefined;
    }
    const t = setTimeout(() => setForceOpen(true), forceModalDelayMs);
    return () => clearTimeout(t);
  }, [supported, permission, forceModalDelayMs, snoozed]);

  const requestPermission = useCallback(async () => {
    const next = await requestBrowserNotificationPermission();
    setPermission(next);
    if (next === "granted") setForceOpen(false);
    return next;
  }, []);

  const dismissForNow = useCallback(() => {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setSnoozed(true);
    setForceOpen(false);
  }, []);

  return {
    supported,
    permission,
    isGranted: permission === "granted",
    isDenied: permission === "denied",
    forceOpen,
    setForceOpen,
    requestPermission,
    dismissForNow,
    refresh,
  };
}
